import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TpRectangleCard from "./components/TpRectangleCard";
import NavBar from "./components/NavBar";
import { FaHeart, FaBookmark } from "react-icons/fa";
import { VscEye } from "react-icons/vsc";
import { IoCompass } from "react-icons/io5";
import {
  getWatchedContent,
  getLikeBookmarkedContent,
  getSolrContentDetail,
  bookmarkContent,
  likeContent,
  getContentCounts,
} from "./service";
import { getDecodedToken, getLocalStorage } from "../../utils/localStorage";
import useStore from "../../store/useStore";
import Bookmark from "../../assets/images/bookmarks.svg";
import Like from "../../assets/images/like-tag.svg";
import EmptyState from "../../assets/images/EmptyState.svg";
import { AUTH_TOKEN } from "../../utils/constants";
import { trackZymedEvent, ZYMED_EVENTS } from "../../utils/zymedAnalytics";

interface ContentItem {
  id: number;
  contentId: number;
  typeId: any;
  updatedDate: string;
  viewedDate: string | null;
  watchedPercent: number | null;
  watchedDuration: number | null;
  searchIdentifier: string;
  isLike: boolean;
  isBookmarked: boolean;
  like_count: number;
  contentTitle: string;
  contentType: string;
  duration: string;
  uploadThumbnailUrl: string;
  contentDescription: string;
  previewDeepLink: string;
  docId: any;
  contentTypeId: any;
  publishedDate: string;
  deepLinkCode: string;
}

// Update the interface for content counts
interface ContentCounts {
  [key: string]: {
    isExternalSharable: number;
    like_count: number;
    uploadThumbnailUrl: string;
    previewUrl: string;
    readTime: string;
    duration: string;
  };
}

function PediaLibrary() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlActiveTab = params.get("activeTab") as "recently-viewed" | "bookmarks" | "likes" | null;
  const [activeTab, setActiveTab] = useState<
    "recently-viewed" | "bookmarks" | "likes"
  >(urlActiveTab || "recently-viewed");
  const [content, setContent] = useState<ContentItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [localLikes, setLocalLikes] = useState<number[]>([]);
  const [localBookmarks, setLocalBookmarks] = useState<number[]>([]);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [contentCounts, setContentCounts] = useState<ContentCounts>({});
  const {
    tatvaPediaToken,
    tatvaPediaBookmarks,
    updateTatvaPediaBookmark,
    updateTatvaPediaLike,
    setTatvaPediaBookmarkedList,
    setTatvaPediaLikedList,
  } = useStore();
  const { doctorDetails, setDoctorDetails } = useStore((state) => ({
    doctorDetails: state.doctorDetails,
    setDoctorDetails: state.setDoctorDetails,
  }));

  const navigate = useNavigate();
  // Add a ref to track pending operations
  const pendingOperations = useRef<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (getLocalStorage(AUTH_TOKEN)) {
      const decodedToken = getDecodedToken();
      setDoctorDetails({ ...doctorDetails, ...decodedToken });
    }
  }, []);

  useEffect(() => {
    if (urlActiveTab && ["recently-viewed", "bookmarks", "likes"].includes(urlActiveTab)) {
      setActiveTab(urlActiveTab);
    }
  }, [urlActiveTab, location.search]);

  useEffect(() => {
    if (!tatvaPediaBookmarks?.likedList || pendingOperations.current["like"])
      return;

    const likedIds = tatvaPediaBookmarks.likedList
      .filter((item: any) => item && item.contentId)
      .map((item: any) => item.contentId);

    setLocalLikes(likedIds);
  }, [tatvaPediaBookmarks.likedList]);

  useEffect(() => {
    if (
      !tatvaPediaBookmarks?.bookmarkedList ||
      pendingOperations.current["bookmark"]
    )
      return;

    const bookmarkedIds = tatvaPediaBookmarks.bookmarkedList
      .filter((item: any) => item && item.contentId)
      .map((item: any) => item.contentId);

    setLocalBookmarks(bookmarkedIds);
  }, [tatvaPediaBookmarks.bookmarkedList]);

  // Add effect to fetch initial liked and bookmarked content
  useEffect(() => {
    const fetchInitialState = async () => {
      if (!tatvaPediaToken) return;

      try {
        // Fetch both liked and bookmarked content
        const [likedResponse, bookmarkedResponse] = await Promise.all([
          getLikeBookmarkedContent("liked", tatvaPediaToken),
          getLikeBookmarkedContent("bookmarked", tatvaPediaToken),
        ]);

        if (likedResponse) {
          setTatvaPediaLikedList(likedResponse);
          const likedIds = likedResponse
            .filter((item: any) => item && item.contentId)
            .map((item: any) => item.contentId);
          setLocalLikes(likedIds);
        }

        if (bookmarkedResponse) {
          setTatvaPediaBookmarkedList(bookmarkedResponse);
          const bookmarkedIds = bookmarkedResponse
            .filter((item: any) => item && item.contentId)
            .map((item: any) => item.contentId);
          setLocalBookmarks(bookmarkedIds);
        }
      } catch (error) {
        console.error("Error fetching initial state:", error);
      }
    };

    fetchInitialState();
  }, [tatvaPediaToken]);

  // Update the content counts effect
  useEffect(() => {
    const fetchContentCounts = async () => {
      if (!content.length || !tatvaPediaToken) return;

      try {
        const contents = content.map((doc: any) => ({
          contentId: doc.docId,
          contentTypeId: doc.contentTypeId,
        }));

        const countsResponse = await getContentCounts({
          contents,
          token: tatvaPediaToken,
          userId: doctorDetails?.user?.id,
        });

        const countsMap = countsResponse.body.reduce((acc: any, item: any) => {
          acc[`${item.docId}_${item.docTypeId}`] = {
            like_count: item.likeCount,
            isExternalSharable: item.isExternalSharable,
            uploadThumbnailUrl: item.uploadThumbnailUrl,
            previewUrl: item.previewUrl,
            readTime: item.readTime,
            duration: item.duration,
          };
          return acc;
        }, {});

        setContentCounts(countsMap);
      } catch (error) {
        console.error("Error fetching content counts:", error);
      }
    };

    fetchContentCounts();
  }, [content, tatvaPediaToken]);

  // Update the content fetching effect to also fetch counts
  useEffect(() => {
    const fetchContent = async () => {
      setIsLoading(true);
      try {
        const token = getToken();
        let response;

        switch (activeTab) {
          case "recently-viewed":
            response = await getLikeBookmarkedContent("viewed", token);
            break;
          case "bookmarks":
            response = await getLikeBookmarkedContent("bookmarked", token);
            if (response) {
              setTatvaPediaBookmarkedList(response);
            }
            break;
          case "likes":
            response = await getLikeBookmarkedContent("liked", token);
            if (response) {
              setTatvaPediaLikedList(response);
            }
            break;
        }

        if (
          response &&
          (activeTab === "bookmarks" ||
            activeTab === "likes" ||
            activeTab === "recently-viewed")
        ) {
          const items = response;
          const docIds = items.map((item: any) => {
            const id = item.searchIdentifier || item.contentId;
            return activeTab === "likes" || activeTab === "recently-viewed"
              ? `${id}_CT`
              : id;
          });

          if (docIds.length > 0) {
            // Only make the API call if we have docIds
            const solrResponse = await getSolrContentDetail({
              docIds,
              start: 0,
              rows: docIds.length,
            });

            if (solrResponse?.response?.docs) {
              setContent(solrResponse.response.docs);

              // Fetch content counts after getting content
              if (token) {
                const contents = solrResponse.response.docs.map((doc: any) => ({
                  contentId: doc.docId,
                  contentTypeId: doc.contentTypeId,
                }));

                const countsResponse = await getContentCounts({
                  contents,
                  token: token,
                  userId: doctorDetails?.user?.id,
                });

                const countsMap = countsResponse.body.reduce(
                  (acc: any, item: any) => {
                    acc[`${item.docId}_${item.docTypeId}`] = {
                      like_count: item.likeCount,
                      isExternalSharable: item.isExternalSharable,
                      uploadThumbnailUrl: item.uploadThumbnailUrl,
                      previewUrl: item.previewUrl,
                      readTime: item.readTime,
                      duration: item.duration,
                    };
                    return acc;
                  },
                  {}
                );

                setContentCounts(countsMap);
              }
            } else {
              setContent([]);
            }
          } else {
            setContent([]); // Set empty content if no docIds
          }
        } else {
          setContent([]);
        }
      } catch (error) {
        console.error("Error fetching content:", error);
        setContent([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [activeTab, tatvaPediaToken]);

  const handleLike = async (content: any, isLiked: boolean) => {
    if (!content?.docId) return;

    try {
      const token = getToken();
      if (!token) return;

      setIsActionLoading(true);
      pendingOperations.current["like"] = true;

      // Optimistically update UI
      setLocalLikes((prev) => {
        const newLikes = isLiked
          ? prev.filter((id) => id !== content.docId)
          : [...prev, content.docId];
        return newLikes;
      });

      // Update content counts
      setContentCounts((prev) => {
        const key = `${content.docId}_${content.contentTypeId}`;
        const currentCount = prev[key] || { like_count: 0 };
        return {
          ...prev,
          [key]: {
            ...currentCount,
            like_count: currentCount.like_count + (isLiked ? -1 : 1),
          },
        };
      });

      // Make API call
      const response = await likeContent(
        content.docId,
        content.contentTypeId,
        !isLiked,
        token
      );

      if (response) {
        // Update global state after successful API call
        updateTatvaPediaLike(content, !isLiked);

        // Fetch updated like list to ensure consistency
        const updatedLikedContent = await getLikeBookmarkedContent(
          "liked",
          token
        );
        if (updatedLikedContent) {
          setTatvaPediaLikedList(updatedLikedContent);

          // If we're on likes tab and successfully removed a like, update the content state
          if (activeTab === "likes" && isLiked) {
            setContent((prevContent) =>
              prevContent.filter((item) => item.docId !== content.docId)
            );
          }
        }
      } else {
        // Revert local states on failure
        setLocalLikes((prev) =>
          isLiked
            ? [...prev, content.docId]
            : prev.filter((id) => id !== content.docId)
        );

        setContentCounts((prev) => {
          const key = `${content.docId}_${content.contentTypeId}`;
          const currentCount = prev[key] || { like_count: 0 };
          return {
            ...prev,
            [key]: {
              ...currentCount,
              like_count: currentCount.like_count + (isLiked ? 1 : -1),
            },
          };
        });
      }
    } catch (error) {
      console.error("Error in like operation:", error);
      // Revert local states on error
      setLocalLikes((prev) =>
        isLiked
          ? [...prev, content.docId]
          : prev.filter((id) => id !== content.docId)
      );

      setContentCounts((prev) => {
        const key = `${content.docId}_${content.contentTypeId}`;
        const currentCount = prev[key] || { like_count: 0 };
        return {
          ...prev,
          [key]: {
            ...currentCount,
            like_count: currentCount.like_count + (isLiked ? 1 : -1),
          },
        };
      });
    } finally {
      setIsActionLoading(false);
      setTimeout(() => {
        pendingOperations.current["like"] = false;
      }, 1500);
    }
  };

  const handleBookmark = async (content: any, isBookmarked: boolean) => {
    if (!content?.docId) return;

    try {
      const token = getToken();
      if (!token) return;

      setIsActionLoading(true);
      pendingOperations.current["bookmark"] = true;

      // Optimistically update UI
      setLocalBookmarks((prev) => {
        const newBookmarks = isBookmarked
          ? prev.filter((id) => id !== content.docId)
          : [...prev, content.docId];
        return newBookmarks;
      });

      // Make API call
      const response = await bookmarkContent(
        content.docId,
        content.contentTypeId,
        !isBookmarked,
        token
      );

      if (response) {
        // Update global state after successful API call
        updateTatvaPediaBookmark(content, !isBookmarked);

        // Fetch updated bookmark list to ensure consistency
        const updatedBookmarkedContent = await getLikeBookmarkedContent(
          "bookmarked",
          token
        );
        if (updatedBookmarkedContent) {
          setTatvaPediaBookmarkedList(updatedBookmarkedContent);

          // If we're on bookmarks tab and successfully removed a bookmark, update the content state
          if (activeTab === "bookmarks" && isBookmarked) {
            setContent((prevContent) =>
              prevContent.filter((item) => item.docId !== content.docId)
            );
          }
        }
      } else {
        // Revert local state on failure
        setLocalBookmarks((prev) =>
          isBookmarked
            ? [...prev, content.docId]
            : prev.filter((id) => id !== content.docId)
        );
      }
    } catch (error) {
      console.error("Error in bookmark operation:", error);
      // Revert local state on error
      setLocalBookmarks((prev) =>
        isBookmarked
          ? [...prev, content.docId]
          : prev.filter((id) => id !== content.docId)
      );
    } finally {
      setIsActionLoading(false);
      setTimeout(() => {
        pendingOperations.current["bookmark"] = false;
      }, 1500);
    }
  };

  const isItemLiked = (item: ContentItem) => {
    if (!item?.docId) return false;
    const isLiked = localLikes.includes(item.docId);
    return isLiked;
  };

  const isItemBookmarked = (item: ContentItem) => {
    if (!item?.docId) return false;
    const isBookmarked = localBookmarks.includes(item.docId);
    return isBookmarked;
  };

  // Helper function to get token
  const getToken = () =>
    localStorage.getItem("tatvaPediaToken") || tatvaPediaToken;

  const handleTabChange = (tab: typeof activeTab) => {
    if (tab === "bookmarks") trackZymedEvent(ZYMED_EVENTS.PEDIA_BOOKMARKS);
    if (tab === "likes") trackZymedEvent(ZYMED_EVENTS.PEDIA_LIKES);
    setActiveTab(tab);
    navigate(`/tatvapedia/library?activeTab=${tab}`, { replace: true });
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Fixed Header Section */}
      <div className="flex-none">
        <div className="bg-white p-4 flex justify-between items-center z-10 text-2xl font-bold text-left">
          Your Library
        </div>

        <div className="shadow-md bg-white">
          <div className="flex px-2 overflow-x-auto">
            <button
              onClick={() => handleTabChange("recently-viewed")}
              className={`relative flex items-center gap-2 pt-3 pb-1 text-sm font-medium rounded-t-lg ${
                activeTab === "recently-viewed" ? "text-indigo-600" : ""
              }`}
            >
              <span
                className={`flex w-max items-center font-roboto text-base px-3 py-2 leading-6 tracking-[0.25px] ${
                  activeTab === "recently-viewed"
                    ? "bg-indigo-50 text-indigo-600 rounded-3xl mb-1 font-bold"
                    : ""
                }`}
              >
                {activeTab === "recently-viewed" && (
                  <div className="mr-1">
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 17 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="Eye">
                        <path
                          id="Shape"
                          d="M8.89542 6.50243C10.3682 6.50243 11.5621 7.69634 11.5621 9.1691C11.5621 10.6419 10.3682 11.8358 8.89542 11.8358C7.42266 11.8358 6.22876 10.6419 6.22876 9.1691C6.22876 7.69634 7.42266 6.50243 8.89542 6.50243ZM8.89542 4.16602C11.9711 4.16602 14.6261 6.26602 15.3628 9.20894C15.4299 9.47682 15.2671 9.74833 14.9992 9.81539C14.7313 9.88245 14.4598 9.71965 14.3928 9.45177C13.7668 6.95132 11.5097 5.16602 8.89542 5.16602C6.28005 5.16602 4.02216 6.95286 3.39733 9.45478C3.33042 9.7227 3.05899 9.88564 2.79108 9.81874C2.52317 9.75183 2.36022 9.4804 2.42713 9.21249C3.16252 6.26783 5.81841 4.16602 8.89542 4.16602Z"
                          fill="#4B4AD5"
                        />
                      </g>
                    </svg>
                  </div>
                )}
                Recently Viewed
              </span>
              {activeTab === "recently-viewed" && (
                <span className="absolute bottom-0 left-1/2 w-[66px] h-[5px] bg-indigo-600 rounded-t-lg -translate-x-1/2"></span>
              )}
            </button>

            <button
              onClick={() => handleTabChange("bookmarks")}
              className={`relative flex items-center gap-2 pt-3 pb-1 text-sm font-medium rounded-t-lg ${
                activeTab === "bookmarks" ? "text-indigo-600" : ""
              }`}
            >
              <span
                className={`flex w-max items-center font-roboto text-base px-3 py-2 leading-6 tracking-[0.25px] ${
                  activeTab === "bookmarks"
                    ? "bg-indigo-50 text-indigo-600 rounded-3xl mb-1 font-bold"
                    : ""
                }`}
              >
                {activeTab === "bookmarks" && (
                  <div className="mr-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="17"
                      height="17"
                      viewBox="0 0 17 17"
                      fill="none"
                    >
                      <path
                        d="M4.62697 15.0698C4.29621 15.3078 3.83496 15.0714 3.83496 14.6639V4.66667C3.83496 3.47005 4.80501 2.5 6.00163 2.5H11.0006C12.1972 2.5 13.1672 3.47005 13.1672 4.66667V14.6639C13.1672 15.0714 12.706 15.3078 12.3752 15.0698L8.50109 12.2825L4.62697 15.0698Z"
                        fill="#4B4AD5"
                      />
                    </svg>
                  </div>
                )}
                Bookmarks
              </span>
              {activeTab === "bookmarks" && (
                <span className="absolute bottom-0 left-1/2 w-[66px] h-[5px] bg-indigo-600 rounded-t-lg -translate-x-1/2"></span>
              )}
            </button>

            <button
              onClick={() => handleTabChange("likes")}
              className={`relative flex items-center gap-2 pt-3 pb-1 text-sm font-medium rounded-t-lg ${
                activeTab === "likes" ? "text-indigo-600" : ""
              }`}
            >
              <span
                className={`flex w-max items-center font-roboto text-base px-3 py-2 leading-6 tracking-[0.25px] ${
                  activeTab === "likes"
                    ? "bg-indigo-50 text-indigo-600 rounded-3xl mb-1 font-bold"
                    : ""
                }`}
              >
                {activeTab === "likes" && (
                  <div className="mr-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="17"
                      height="17"
                      viewBox="0 0 17 17"
                      fill="none"
                    >
                      <path
                        d="M6.15625 12.3734V5.60003C6.15625 5.33336 6.23625 5.07336 6.38292 4.85336L8.20292 2.1467C8.48958 1.71336 9.20292 1.4067 9.80958 1.63336C10.4629 1.85336 10.8962 2.5867 10.7562 3.24003L10.4096 5.42003C10.3829 5.62003 10.4362 5.80003 10.5496 5.94003C10.6629 6.0667 10.8296 6.1467 11.0096 6.1467H13.7496C14.2762 6.1467 14.7296 6.36003 14.9962 6.73336C15.2496 7.09336 15.2962 7.56003 15.1296 8.03336L13.4896 13.0267C13.2829 13.8534 12.3829 14.5267 11.4896 14.5267H8.88958C8.44292 14.5267 7.81625 14.3734 7.52958 14.0867L6.67625 13.4267C6.34958 13.18 6.15625 12.7867 6.15625 12.3734Z"
                        fill="#4B4AD5"
                      />
                      <path
                        d="M4.03551 4.30078H3.34884C2.31551 4.30078 1.89551 4.70078 1.89551 5.68745V12.3941C1.89551 13.3808 2.31551 13.7808 3.34884 13.7808H4.03551C5.06884 13.7808 5.48884 13.3808 5.48884 12.3941V5.68745C5.48884 4.70078 5.06884 4.30078 4.03551 4.30078Z"
                        fill="#4B4AD5"
                      />
                    </svg>
                  </div>
                )}
                Likes
              </span>
              {activeTab === "likes" && (
                <span className="absolute bottom-0 left-1/2 w-[66px] h-[5px] bg-indigo-600 rounded-t-lg -translate-x-1/2"></span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Content Section */}
      <div className="flex-1 overflow-y-auto">
        {(isLoading || isActionLoading) && (
          <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        )}
        <div className="px-4 mt-4 mb-20">
          {content.length > 0 ? (
            <div className="space-y-4">
              {content.map((item: ContentItem) => (
                <TpRectangleCard
                  key={item.id}
                  isShowcontentType={true}
                  isLike={isItemLiked(item)}
                  isBookmarked={isItemBookmarked(item)}
                  onLike={() => handleLike(item, isItemLiked(item))}
                  onBookmark={() =>
                    handleBookmark(item, isItemBookmarked(item))
                  }
                  data={{
                    docId: item.docId,
                    contentTitle: item.contentTitle,
                    contentType: item.contentType,
                    uploadThumbnailUrl: item.uploadThumbnailUrl,
                    contentDescription: item.contentDescription,
                    previewDeepLink: item.previewDeepLink,
                    like_count:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.like_count ||
                      item.like_count ||
                      0,
                    contentTypeId: item.contentTypeId,
                    publishedDate: item.publishedDate,
                    isExternalSharable:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.isExternalSharable || 0,
                    previewUrl:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.previewUrl ||
                      item?.previewDeepLink ||
                      "",
                    readTime:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.readTime || "",
                    duration:
                      contentCounts[`${item.docId}_${item.contentTypeId}`]
                        ?.duration || "",
                    deepLinkCode: item.deepLinkCode,
                    source: "/tatvapedia/library",
                    currentTab: activeTab
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center h-[40vh] justify-center mt-20">
              {activeTab === "recently-viewed" && (
                <>
                  <img
                    src={EmptyState}
                    alt="Nothing here yet"
                    className="w-32 h-32 mb-4"
                  />
                  <p className="text-[#2D2D2D] text-2xl font-bold mb-2 text-center">
                    Nothing Here Yet!
                  </p>
                  <p className="text-gray-600 text-center">
                    Looks like you haven't explored any content yet. Start
                    browsing and we'll keep track of what you view here.
                  </p>
                </>
              )}
              {activeTab === "bookmarks" && (
                <>
                  <img
                    src={Bookmark}
                    alt="No bookmarks found"
                    className="w-32 h-32 mb-4"
                  />
                  <p className="text-[#2D2D2D] text-2xl font-bold mb-2 text-center">
                    No Bookmarks Found!
                  </p>
                  <p className="text-gray-600 text-center">
                    Save your favorite reads and listens by tapping the bookmark
                    icon. They'll show up here for easy access anytime.
                  </p>
                </>
              )}
              {activeTab === "likes" && (
                <>
                  <img
                    src={Like}
                    alt="No likes yet"
                    className="w-32 h-32 mb-4"
                  />
                  <p className="text-[#2D2D2D] text-2xl font-bold mb-2 text-center">
                    You Haven't Liked Anything Yet!
                  </p>
                  <p className="text-gray-600 text-center">
                    Tap the thumbs-up icon on any content you love. All your
                    liked items will appear here.
                  </p>
                </>
              )}
              <button
                onClick={() => navigate("/tatvapedia")}
                className="mt-8 flex items-center justify-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-full font-medium"
              >
                <span className="material-icons text-xl">
                  <IoCompass className="w-6 h-6" />
                </span>
                Discover Content
              </button>
            </div>
          )}
        </div>
      </div>

      <NavBar />
    </div>
  );
}

export default PediaLibrary;
